import React, { forwardRef, ForwardedRef, Ref, useRef } from 'react';
import clsx from 'clsx';
import { useFocusRing } from '@react-aria/focus';
import { useFocusableRef } from '@react-spectrum/utils';
import { MultipleSelection, Node, Selection } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/select/select.module.css';

import { SelectBase } from './SelectBase';
import { SelectProps } from './SelectProps';
import { useMultiSelect } from './useMultiSelect';
import { useMultiSelectState } from './useMultiSelectState';
import { MultipleSelectionKeys } from '../../utils/types';
import {
  SapphireSelectionManagerOptions,
  WithResolveSelectAllToKeysKeys,
} from '../../utils/patchSelectionManager';

export type MultiSelectProps<T> = Omit<
  SelectProps<T>,
  | 'onSelectionChange'
  | 'defaultSelectedKey'
  | 'selectedKey'
  // We omit these for now, as multiple selection components work differently
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'selectionMode'
> &
  WithResolveSelectAllToKeysKeys<
    Pick<MultipleSelection, MultipleSelectionKeys>
  > & {
    /**
     * @deprecated
     */
    selectionMode?: MultipleSelection['selectionMode'];
    /**
     * The function to customize the rendered text in the field for a given selection.
     *
     * Note that if there is only one item seleted, that one is rendered in the field.
     *
     * Note that if the selection is the string "all" it refers to the explicit
     * choice of selecting all items (even if not all were rendered), this can
     * be caused by the user pressing ctrl+a or cmd+a.
     *
     * @default (selection, selectedItems) => selection === 'all' ? 'All selected' : `${selection.size} selected`
     *
     */
    renderSelectionText?: (
      selection: Selection,
      selectedItems: Node<T>[]
    ) => string;
  } & Pick<SapphireSelectionManagerOptions, 'resolveSelectAllToKeys'>;

/**
 * Allows user to select one or more options from a list.
 *
 * @deprecated Use Select with `selectionMode="multiple"` instead.
 *
 * Example:
 * ```tsx
 * <MultiSelect label="Label text" placeholder="Select an option">
 *  <SelectItem>Option 1</SelectItem>
 *  <SelectItem>Option 2</SelectItem>
 *  <SelectItem>Option 3</SelectItem>
 *  <SelectItem>Option 4</SelectItem>
 * </MultiSelect>
 * ```
 */

function _MultiSelect<T extends object>(
  { onSelectionChange, ...props }: MultiSelectProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useFocusableRef<HTMLDivElement>(null);
  const listBoxRef = useFocusableRef<HTMLUListElement>(null);
  const {
    renderSelectionText = (selection) =>
      selection === 'all' ? 'All selected' : `${selection.size} selected`,
    size = 'lg',
    labelPlacement = 'above',
  } = props;

  const state = useMultiSelectState({
    ...props,
    // @ts-expect-error -- due to the SapphireSelectionManager, the type of onSelectionChange is different, sometimes it only returns Set<Keys> apart from the original "Selection" type. We decided to own this type error, instead of passing it down to the user.
    onSelectionChange,
  });
  const selectAria = useMultiSelect(
    {
      ...props,
      errorMessage: props.error,
      description: props.note,
      // @ts-expect-error -- due to the SapphireSelectionManager, the type of onSelectionChange is different, sometimes it only returns Set<Keys> apart from the original "Selection" type. We decided to own this type error, instead of passing it down to the user.
      onSelectionChange,
    },
    state,
    triggerRef
  );
  const focusRingAria = useFocusRing();

  return (
    <SelectBase
      multiple
      containerRef={ref}
      popoverRef={popoverRef}
      listBoxRef={listBoxRef}
      triggerRef={triggerRef}
      selectProps={props}
      state={state}
      selectAria={selectAria}
      focusRingAria={focusRingAria}
      size={size}
      labelPlacement={labelPlacement}
      selectValueContent={
        state.selectionManager.rawSelection === 'all' ? (
          renderSelectionText('all', state.selectedItems)
        ) : state.selectedItems.length === 0 ? (
          <div className={clsx(styles['sapphire-select__placeholder'])}>
            {props.placeholder}
          </div>
        ) : state.selectedItems.length === 1 ? (
          <>{state.selectedItems[0].rendered}</>
        ) : (
          <>
            {renderSelectionText(
              state.selectionManager.selectedKeys,
              state.selectedItems
            )}
          </>
        )
      }
    />
  );
}
export const MultiSelect = forwardRef(_MultiSelect) as <T extends object>(
  props: MultiSelectProps<T> & { ref?: Ref<HTMLDivElement> }
) => React.ReactElement;
